import { create } from "zustand";
import {
  applyResolvedTheme,
  readThemePreference,
  resolveTheme,
  subscribeToSystemTheme,
  writeThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "@/lib/theme";

interface ThemeStore {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  /**
   * Flips the visible theme. Landing on the OS theme stores `system`, so the
   * portal follows OS changes again instead of pinning an explicit choice.
   */
  toggle: () => void;
}

const initialPreference = readThemePreference();
const initialResolved = resolveTheme(initialPreference);
// The pre-paint script in index.html is best effort; make <html> match the store.
applyResolvedTheme(initialResolved);

export const useThemeStore = create<ThemeStore>((set, get) => ({
  preference: initialPreference,
  resolved: initialResolved,
  toggle: () => {
    const resolved: ResolvedTheme = get().resolved === "dark" ? "light" : "dark";
    const preference: ThemePreference =
      resolved === resolveTheme("system") ? "system" : resolved;
    writeThemePreference(preference);
    applyResolvedTheme(resolved);
    set({ preference, resolved });
  },
}));

/** Re-resolves the theme after an OS theme change, unless the user pinned one. */
export function syncWithSystemTheme(): void {
  const { preference } = useThemeStore.getState();
  if (preference !== "system") {
    return;
  }
  const resolved = resolveTheme(preference);
  applyResolvedTheme(resolved);
  useThemeStore.setState({ resolved });
}

subscribeToSystemTheme(syncWithSystemTheme);
